import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { authApi } from "../api/endpoints";
import { AuthLayout } from "../components/Layout";
import { Alert, Button, TextField } from "../components/ui";
import { useDocumentTitle, useMutation } from "../hooks/useApi";

const MIN_LENGTH = 12;

/** Diagnostic local, aligné sur la politique appliquée par le serveur. */
function passwordIssues(password: string): string[] {
  const issues: string[] = [];
  if (password.length < MIN_LENGTH) {
    issues.push(`au moins ${MIN_LENGTH} caractères`);
  }
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^\w\s]/].filter((pattern) =>
    pattern.test(password),
  ).length;
  if (classes < 3) {
    issues.push(
      "au moins 3 types de caractères parmi minuscules, majuscules, chiffres et symboles",
    );
  }
  if (/(.)\1{3,}/.test(password)) {
    issues.push("pas de caractère répété 4 fois de suite");
  }
  return issues;
}

export function RegisterPage() {
  useDocumentTitle("Créer un compte");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const issues = useMemo(() => passwordIssues(password), [password]);
  const mismatch =
    confirmation.length > 0 && confirmation !== password
      ? "Les deux mots de passe ne correspondent pas."
      : undefined;

  const register = useMutation(
    () =>
      authApi.register({
        email: email.trim(),
        password,
        display_name: displayName.trim() || undefined,
      }),
    { onSuccess: () => setSubmitted(true) },
  );

  if (submitted) {
    return (
      <AuthLayout
        title="Vérifiez votre boîte e-mail"
        footer={<Link to="/connexion">Retour à la connexion</Link>}
      >
        <Alert tone="success" title="Compte créé">
          Un e-mail de vérification vient d'être envoyé à{" "}
          <strong>{email.trim()}</strong>. Ouvrez le lien qu'il contient pour
          activer votre compte, puis connectez-vous.
        </Alert>
        <p className="mt-4 text-muted">
          Vous ne recevez rien ? Vérifiez vos courriers indésirables, puis
          demandez un nouvel envoi depuis la page de connexion.
        </p>
      </AuthLayout>
    );
  }

  const canSubmit =
    email.trim().length > 3 &&
    password.length > 0 &&
    issues.length === 0 &&
    !mismatch &&
    confirmation.length > 0;

  return (
    <AuthLayout
      title="Créer un compte"
      intro="Une adresse e-mail et un mot de passe suffisent pour commencer. Le profil de votre entreprise sera configuré ensuite."
      footer={<Link to="/connexion">J'ai déjà un compte</Link>}
    >
      {register.error && (
        <Alert tone="critical" title="Création impossible">
          {register.error.message}
        </Alert>
      )}

      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) void register.run();
        }}
        noValidate
      >
        <TextField
          label="Adresse e-mail"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={register.fieldErrors.email}
          hint="Elle servira à vous connecter et à recevoir les rappels d'échéance."
        />
        <TextField
          label="Nom affiché"
          optional
          autoComplete="name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={register.fieldErrors.display_name}
        />
        <TextField
          label="Mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={register.fieldErrors.password}
          hint={
            password.length === 0
              ? `Au moins ${MIN_LENGTH} caractères, avec au moins 3 types de caractères différents.`
              : issues.length === 0
                ? "Ce mot de passe respecte la politique de sécurité."
                : `Il manque : ${issues.join(", ")}.`
          }
        />
        <TextField
          label="Confirmation du mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          error={mismatch}
        />
        <Button
          type="submit"
          variant="primary"
          block
          loading={register.pending}
          disabled={!canSubmit}
        >
          Créer mon compte
        </Button>
      </form>
    </AuthLayout>
  );
}
